/**
 * Catálogo canónico das 13 categorias verticais do PRD §5.1.1.
 *
 * 🔴 FRONTEIRA COM O M2 — leia antes de alterar.
 *
 * A tabela `service_categories` só nasce no M2 (T2.1), mas o onboarding consumer (T1.14) já grava
 * `consumer_profiles.favorite_categories uuid[]` no M1. Para que as escolhas feitas agora continuem
 * válidas quando a tabela existir, os IDs são **UUID v5 determinísticos**, derivados do namespace
 * abaixo mais o slug — qualquer máquina recalcula exatamente os mesmos 13 valores.
 *
 * **Obrigação da T2.1:** criar `service_categories` com ESTES IDs. Gerar IDs novos deixa órfã toda
 * categoria escolhida durante o M1, sem erro visível — o array continua bem formado, apenas aponta
 * para linhas que não existem.
 *
 * Os **nomes legíveis** não vivem aqui: estão no i18n (`onboarding.categories.<slug>`), em pt-PT,
 * en, es e fr. Este catálogo guarda identidade, não texto.
 */

/** Namespace de derivação dos UUID v5. Documentado para a T2.1 poder reproduzir os mesmos IDs. */
export const SERVICE_CATEGORY_NAMESPACE = 'powerful-connect/service-categories';

export interface ServiceCategory {
  readonly id: string;
  readonly slug: string;
}

/** As 13 categorias, na ordem em que o PRD §5.1.1 as enumera. */
export const SERVICE_CATEGORIES = [
  { id: '24585c5c-7d3d-5c8b-878c-79c1445c89a4', slug: 'cabelo' },
  { id: '3b37822f-5e91-554d-bb9b-14da4f5f5a51', slug: 'unhas' },
  { id: '97a02004-7069-568d-a6df-d2679802d712', slug: 'lash-sobrancelha' },
  { id: 'c9a3fead-d84e-5c1e-8a8d-c1896ffb6e2e', slug: 'estetica-facial' },
  { id: 'a6d8cb35-2e97-55a9-955d-dd527d718347', slug: 'estetica-corporal' },
  { id: 'b87f71bf-bf93-5127-87a8-c218c8208074', slug: 'depilacao' },
  { id: 'ea249b74-966d-53b8-9ef1-ed7bcb701a49', slug: 'barbearia' },
  { id: '8c937d1e-ec5b-5a81-92b5-179f3257c908', slug: 'maquiagem' },
  { id: '7300bd77-261a-5d24-ba85-d33bd6846eb5', slug: 'spa-massagem' },
  { id: 'd050eab7-e41c-5f07-b0a6-2767501198d7', slug: 'dermatologia-estetica' },
  { id: 'bd471085-bcc3-5da2-826b-ef19b0fa1cc8', slug: 'odontologia-estetica' },
  { id: 'ecb5473b-9518-5bde-9c84-332a994041b7', slug: 'nutricao-estetica' },
  { id: '9fa0ac99-0a74-5355-a047-a0e29976cea6', slug: 'fisioterapia-estetica' },
] as const satisfies readonly ServiceCategory[];

export type ServiceCategorySlug = (typeof SERVICE_CATEGORIES)[number]['slug'];
export type ServiceCategoryId = (typeof SERVICE_CATEGORIES)[number]['id'];

/** IDs do catálogo, na ordem do PRD. */
export const SERVICE_CATEGORY_IDS = SERVICE_CATEGORIES.map(
  (category) => category.id,
) as readonly ServiceCategoryId[];

const BY_ID = new Set<string>(SERVICE_CATEGORY_IDS);
const BY_SLUG = new Map<string, ServiceCategory>(
  SERVICE_CATEGORIES.map((category) => [category.slug, category]),
);

/**
 * Confirma que o valor é um ID do catálogo.
 *
 * O formato de UUID não basta: `favorite_categories` só pode conter categorias que existem, e um
 * UUID bem formado vindo do cliente continua a ser lixo se não estiver nesta lista.
 */
export function isServiceCategoryId(value: string): value is ServiceCategoryId {
  return BY_ID.has(value);
}

/** Devolve a categoria de um slug, ou `undefined` se o slug não existir. */
export function serviceCategoryBySlug(slug: ServiceCategorySlug): ServiceCategory | undefined {
  return BY_SLUG.get(slug);
}
